import { EntitySearchType } from '@api/api/entity-constants';
import {
  Mention,
  MentionComposerInputHandle,
  parseMentionMarkers,
  SerializedEditorContent,
  toMentionPlainText,
} from '@design/chat/mention-composer';
import {
  ActionHandlers,
  ChatDocumentContextInput,
  ChatInputConfig,
  ChatSubmitEvent,
  PROGRAMMATIC_SUBMIT_EVENT,
} from '@terminal/components/chat';
import { useDropdownKeyboardEvents } from '@terminal/hooks/use-dropdown-keyboard-events/useDropdownKeyboardEvents';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useScheduleDrawerUrlSync } from '@terminal/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useTagsSearch } from '@terminal/hooks/use-tags-search/useTagsSearch';
import {
  EMAIL_TRACKING_PARAM_KEYS,
  queryKeys,
} from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '@terminal/services/tracking/posthog/constants';
import { PromptRefinerTrackingArgs } from '@terminal/services/tracking/posthog/use-posthog-events/alfredEvents';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { useScheduleDrawerStore } from '@terminal/stores/use-schedule-drawer-store/useScheduleDrawerStore';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { toRequestMentions } from '@terminal/views/alfred/research/prompt-form/utils/toRequestMentions';
import { ScheduledQuerySettingsDrawer } from '@terminal/views/alfred/research/scheduled-query-settings-drawer/ScheduledQuerySettingsDrawer';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useThemeSearch } from '@terminal/widgets/search-asset-widget/hooks/use-theme-search/useThemeSearch';
import clsx from 'clsx';
import React, {
  ForwardRefExoticComponent,
  forwardRef,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ChatActionButtons } from '../../components/chat-action-buttons/ChatActionButtons';
import { ChatInputDropdown } from '../chat-input-dropdown/ChatInputDropdown';
import { UploadDisclaimer } from '../upload-disclaimer/UploadDisclaimer';
import styles from './ChatInputComposer.module.scss';
import { PromptRefinementModal } from './prompt-refinement-modal/PromptRefinementModal';
import { decodeQuestion, isAssetNameMessage } from './utils';

export type PromptRefinementContext = Omit<PromptRefinerTrackingArgs, 'source'>;

const getAddedMentions = ({
  previous,
  next,
}: {
  previous: Mention[];
  next: Mention[];
}): Mention[] => {
  const existingIds = new Set(previous.map(mention => mention.id));
  return next.filter(mention => !existingIds.has(mention.id));
};

const mentionsFromMarkerText = (text: string): Mention[] | undefined => {
  const uniqueMentions = new Map<string, Mention>();

  for (const segment of parseMentionMarkers(text)) {
    if (typeof segment !== 'string') {
      uniqueMentions.set(`${segment.type}:${segment.id}`, segment);
    }
  }

  return toRequestMentions([...uniqueMentions.values()]);
};

const currentRequestMentions = (
  mentionsEnabled: boolean,
  content: SerializedEditorContent
): Mention[] | undefined =>
  mentionsEnabled ? toRequestMentions(content.mentions) : undefined;

const mentionTriggerType = (mention: Mention): '@' | '#' =>
  mention.type === 'entity' ||
  mention.type === 'theme' ||
  mention.type.startsWith('insight')
    ? '@'
    : '#';

export interface ChatInputComposerProps {
  className?: string;
  handlers: ActionHandlers;
  config: ChatInputConfig & {
    isLoading: boolean;
  };
  trackingMetadata?: TrackingMetadata;
  promptRefinementContext?: PromptRefinementContext;
}

const ChatInputComposerBase = forwardRef<
  HTMLTextAreaElement,
  ChatInputComposerProps
>(
  (
    {
      handlers,
      config,
      className,
      trackingMetadata,
      promptRefinementContext,
    }: ChatInputComposerProps,
    ref
  ) => {
    const { t } = useTranslation(['chat']);
    const { params, deleteQueryParam } = useQueryParams();
    const { trackPromptRefinerOpened, trackAlfredMentionSelected } =
      usePosthogEvents();
    const isJapaneseUser = useLanguageStore(
      state => state.languageSetting === 'ja' || state.languageSetting === 'jp'
    );
    const isPromptRefinementEnabled =
      !isJapaneseUser && (config.enablePromptRefinement ?? true);
    const trackingSource =
      trackingMetadata?.source ?? POSTHOG_TRACKING_SOURCES.ALFRED;
    const refinerTracking: PromptRefinerTrackingArgs = {
      source: trackingSource,
      conversationId: promptRefinementContext?.conversationId,
      messageId: promptRefinementContext?.messageId,
    };

    const isAssetMessage = useRef(false);
    const [inputValue, setInputValue] = useState('');
    const [isPromptRefinementOpen, setIsPromptRefinementOpen] = useState(false);
    const [showItems, setShowItems] = useState(true);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const mentionInputRef = useRef<MentionComposerInputHandle>(null);
    const mentionsContentRef = useRef<SerializedEditorContent>({
      text: '',
      mentions: [],
    });
    const hasPrefilledQuestionRef = useRef(false);
    const { mode } = useChatNavigation();
    const isBasicUser = useSubscription(state => state.isBasicUser());

    const { autoFocus: shouldAutoFocus = true } = config;
    const encodedQuestion = params.question;
    const { mentionsEnabled } = useFlags(state => ({
      mentionsEnabled: state.getFeatureFlagState('REASON-2103-alfred-mentions'),
    }));
    const { isOpen: isScheduleDrawerOpen, close: closeScheduleDrawer } =
      useScheduleDrawerUrlSync();
    const scheduleDrawerKey = useScheduleDrawerStore(state => state.drawerKey);
    const dropdownApiRef = useDropdownKeyboardEvents(undefined, {
      enabled: !mentionsEnabled,
    });

    // quickAnalysis -- enables asset search + quickAnalysis streaming API
    // quickAnalysis + research -- enables everything, only on dashboard
    // empty -- only question, quickAnalysis streaming API
    const allowedModes = config.allowedModes ?? ['quickAnalysis'];
    const enableAssetSearch =
      !mentionsEnabled &&
      mode === 'quickAnalysis' &&
      allowedModes.includes('quickAnalysis');
    const enableThemeSearch =
      enableAssetSearch && !!(config.enableThemeSearch ?? true);

    const {
      tags: tagList,
      searchTags,
      clearTags,
    } = useTagsSearch({
      searchType: EntitySearchType.Entity,
      additionalConditionChecker: isAssetNameMessage,
    });
    const { themeSearchResults, handleTextChange } = useThemeSearch(
      false,
      enableThemeSearch
    );

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => inputRef.current,
      [inputRef]
    );

    const clearInput = () => {
      setInputValue('');
    };

    const clearMentionsInput = () => {
      mentionInputRef.current?.clear();
      mentionsContentRef.current = { text: '', mentions: [] };
    };

    const clearDropdown = () => {
      clearTags();
      setShowItems(false);

      if (!config?.persistInputTextOnSubmit) {
        setInputValue('');
      }
    };

    const onSubmitCallbackHandler = () => {
      if (!config?.persistInputTextOnSubmit) {
        clearInput();
        if (mentionsEnabled) {
          clearMentionsInput();
        }
      }

      clearDropdown();

      if (!mentionsEnabled && inputRef.current) {
        inputRef.current.rows = 1;
      }
    };

    const onChatSubmit = (event: ChatSubmitEvent) => {
      event.stopPropagation();
      // Capture before clear — mentions live in an uncontrolled editor + ref.
      const submittedValue = mentionsEnabled
        ? mentionsContentRef.current.text
        : inputValue;
      const submittedMentions = mentionsEnabled
        ? toRequestMentions(mentionsContentRef.current.mentions)
        : undefined;

      handlers.onSubmit(
        event,
        submittedValue,
        onSubmitCallbackHandler,
        submittedMentions ? { mentions: submittedMentions } : undefined
      );
    };

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      isAssetMessage.current = isAssetNameMessage(value);
      setInputValue(value);
      setShowItems(true);

      if (enableAssetSearch) {
        searchTags(value);
      }

      if (enableThemeSearch) {
        handleTextChange(value);
      }
    };

    const onMentionsChange = (content: SerializedEditorContent) => {
      const addedMentions = getAddedMentions({
        previous: mentionsContentRef.current.mentions,
        next: content.mentions,
      });
      mentionsContentRef.current = content;
      setInputValue(content.text);
      setShowItems(true);

      for (const mention of addedMentions) {
        trackAlfredMentionSelected({
          source: trackingSource,
          type: mentionTriggerType(mention),
          mentionType: mention.type,
          mentionId: mention.id,
          mentionLabel: mention.label,
        });
      }
    };

    const onMentionsSubmit = () => {
      onChatSubmit(PROGRAMMATIC_SUBMIT_EVENT);
    };

    useEffect(() => {
      if (!encodedQuestion) {
        return;
      }

      const question = decodeQuestion(encodedQuestion);
      const paramsToDelete =
        params[queryKeys.utmSource] === POSTHOG_TRACKING_SOURCES.EMAIL
          ? ['question', ...EMAIL_TRACKING_PARAM_KEYS]
          : 'question';

      deleteQueryParam(paramsToDelete, { replace: true });

      if (!question) {
        return;
      }

      hasPrefilledQuestionRef.current = true;
      isAssetMessage.current = isAssetNameMessage(question);
      setInputValue(question);
      setShowItems(true);

      if (mentionsEnabled) {
        mentionInputRef.current?.setText(question);
        mentionInputRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, [deleteQueryParam, encodedQuestion, mentionsEnabled, params]);

    const handleSelectRefinedPrompt = (prompt: string) => {
      setIsPromptRefinementOpen(false);
      clearInput();

      if (mentionsEnabled) {
        clearMentionsInput();
      }

      handlers.onSubmit(
        PROGRAMMATIC_SUBMIT_EVENT,
        prompt,
        onSubmitCallbackHandler,
        {
          isPromptRefined: true,
          mentions: mentionsFromMarkerText(prompt),
        }
      );
    };

    const handleSelectRefinedPrompts = (prompts: string[]) => {
      const selectedPrompts = prompts
        .map(prompt => prompt.trim())
        .filter(Boolean);

      if (selectedPrompts.length === 0) {
        return;
      }

      setIsPromptRefinementOpen(false);
      clearInput();

      if (mentionsEnabled) {
        clearMentionsInput();
      }

      selectedPrompts.forEach(prompt => {
        handlers.onSubmit(
          PROGRAMMATIC_SUBMIT_EVENT,
          prompt,
          onSubmitCallbackHandler,
          {
            isPromptRefined: true,
            startNewConversation: true,
            mentions: mentionsFromMarkerText(prompt),
          }
        );
      });
    };

    const handleOpenPromptRefinement = () => {
      trackPromptRefinerOpened(refinerTracking);
      setIsPromptRefinementOpen(true);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        if (dropdownApiRef.current?.focusedItemIndex === -1) {
          event.stopPropagation();
          onChatSubmit(event);
        }
      }
    };

    useEffect(() => {
      if (hasPrefilledQuestionRef.current) {
        hasPrefilledQuestionRef.current = false;
        return;
      }

      if (!shouldAutoFocus) {
        clearInput();
        if (mentionsEnabled) {
          clearMentionsInput();
        }
        return;
      }

      if (mentionsEnabled) {
        mentionInputRef.current?.focus();
      } else if (inputRef.current) {
        inputRef.current.focus();
      } else {
        clearInput();
      }
    }, [
      inputRef,
      mentionsEnabled,
      params.mode,
      params.conversationId,
      shouldAutoFocus,
    ]);

    const modePlaceholder = allowedModes.length
      ? t('chat:placeholder', { context: mode })
      : undefined;
    const placeholderText =
      mentionsEnabled && mode === 'research'
        ? t('chat:placeholder_researchMentions')
        : modePlaceholder;
    const showPromptRefinement =
      isPromptRefinementEnabled &&
      !!inputValue.trim() &&
      !config.isLoading &&
      !config.isCustomizedDisabled &&
      !isBasicUser;
    const allowMultiSelect = mode === 'research';

    return (
      <form
        onSubmit={onChatSubmit}
        className={clsx(styles.form, className)}
        data-chat-input-form
      >
        <div
          className={clsx(styles.loaderWrapper, {
            [styles.isMessagePending]: config.isLoading,
          })}
        >
          <ChatInputDropdown
            config={config}
            handlers={{ ...handlers, onSubmitCallbackHandler }}
            inputValue={inputValue}
            tagList={tagList}
            themesList={themeSearchResults}
            clearDropdown={clearDropdown}
            isAssetMessage={isAssetMessage}
            showItems={showItems && !mentionsEnabled}
            setInputValue={setInputValue}
            dropdownApiRef={dropdownApiRef}
          >
            <ChatDocumentContextInput
              inputRef={inputRef}
              data-testid="chat-input"
              autoFocus={shouldAutoFocus}
              placeholder={config?.placeholder ?? placeholderText}
              value={inputValue}
              onChange={onInputChange}
              disabled={config.isCustomizedDisabled}
              isMessagePending={config.isLoading}
              onKeyDownProp={e => {
                onKeyDown(e);
              }}
              enableMentions={mentionsEnabled}
              mentionInputRef={mentionInputRef}
              onMentionsChange={onMentionsChange}
              onMentionsSubmit={onMentionsSubmit}
            >
              <ChatActionButtons
                inputValue={inputValue}
                config={config}
                isMessagePending={config.isLoading}
                abortRequest={handlers.onAbort}
                showRefineButton={showPromptRefinement}
                onRefineClick={handleOpenPromptRefinement}
              />
            </ChatDocumentContextInput>
          </ChatInputDropdown>
        </div>
        <div className={styles.footer}>
          <UploadDisclaimer />
        </div>
        {isPromptRefinementEnabled && (
          <PromptRefinementModal
            isOpen={isPromptRefinementOpen}
            question={toMentionPlainText(inputValue, mentionsEnabled)}
            mentions={currentRequestMentions(
              mentionsEnabled,
              mentionsContentRef.current
            )}
            onClose={() => setIsPromptRefinementOpen(false)}
            onSelectPrompt={handleSelectRefinedPrompt}
            onSelectPrompts={handleSelectRefinedPrompts}
            tracking={refinerTracking}
            allowMultiSelect={allowMultiSelect}
          />
        )}
        {config.alfredEverywhere && (
          <ScheduledQuerySettingsDrawer
            key={scheduleDrawerKey}
            isOpen={isScheduleDrawerOpen}
            onClose={closeScheduleDrawer}
          />
        )}
      </form>
    );
  }
);

export const ChatInputComposer: ForwardRefExoticComponent<
  ChatInputComposerProps & RefAttributes<HTMLTextAreaElement>
> = ChatInputComposerBase;
